import React from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import qs from 'querystring'
import linkTypes from './enums/linkTypes'
import Thing from './utilities/Data/Thing'
import icons from './enums/icons'
import ImageCarousel from './components/ImageCarousel'
import connectPropTypes from './utilities/connectPropTypes'
import getField from './utilities/Data/getField'
import deviceDetect from './utilities/deviceDetect'
import downloadImages from './utilities/downloadImages'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import getPostTitle from './utilities/getPostTitle'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import settings from '../config/settings'
import styles from './PostShare.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'

const mailToHref = ({ subject, body, to }) => {
	const params = qs.stringify({ subject, body })
	return `mailto:${to || ''}?${params}`
}

const getPostImages = (post) => {
	let images
	if (post) {
		images = (((getField(post, 'images') || []).concat(getField(post, 'tags.0.images') || [])) || []).filter(img => img.type === 'full').map(t => t.url)
	}
	return images && images.length > 0 ? images : undefined
}

const shortUserUrl = userName => `${settings.firebase.config.authDomain}/u/${userName}`

class PostShare extends React.Component {
	constructor(props, context) {
		super(props)
		const parsedSearch = getNormalisedParameters(this.props.location.search)
		const that = { context }

		this.state = {
			postId: getDecodedURIComponent(parsedSearch.postid || props.match.params.postId),
			copyText: that.context.translate('Copy text to clipboard'),
			imageIndex: 0,
			images: undefined
		}

		this.postUrl = this.postUrl.bind(this)
		this.getMessage = this.getMessage.bind(this)
		this.handleOnCopy = this.handleOnCopy.bind(this)
		this.shareFacebookKitUrl = this.shareFacebookKitUrl.bind(this)
		this.downloadImageBinder = this.downloadImageBinder.bind(this)
	}

	componentDidMount() {
		this.postTitle = getPostTitle(this.props.user, this.props.post && this.props.post.timestamp ? new Date(this.props.post.timestamp) : new Date())
		this.props.appHeading({
			text: this.postTitle,
			icon: icons.whiteActivity
		})
		this.props.appBottomBarValue(linkTypes.ADD)
		if (this.state.postId && (!this.props.post || this.props.post.postId !== this.state.postId)) {
			// postId specified and no post in state, or the wrong post
			this.props.postFetch({ postId: this.state.postId, uid: this.props.user.uid })
		}
	}

	componentDidUpdate() {
		if (this.props.post && this.props.post.postId === this.state.postId) {
			let kitId = getField(this.props.post, 'tags')
			kitId = kitId && kitId.filter(tag => Thing.isKit(tag))
			kitId = (kitId && kitId[0] && kitId[0].kitId) || undefined

			if (!kitId) {
				this.props.appMessage(messageTypes.ERROR, this.context.translate('Kit not found in post'), errorTypes.UNKNOWNERROR)
			} else if (!this.props.kitForSharing) {
				return this.props.kitShareFetch({ kitId })
			}
			if (!this.state.images) {
				const images = getPostImages(this.props.post)
				if (images) {
					this.setState(() => ({ images })) // eslint-disable-line react/no-did-update-set-state
				} else if (this.props.kit || this.props.kitForSharing) {
					const kit = this.props.kit || this.props.kitDraft || this.props.kitForSharing
					let kitImage = kit && (kit.images || kit.imageUrl)
					if (Array.isArray(kitImage)) {
						kitImage = kitImage.map(ki => ki.url)[0]
					}
					this.setState(() => ({ images: [kitImage] })) // eslint-disable-line react/no-did-update-set-state
				}
			}
		}
		return undefined
	}

	componentWillUnmount() {
		clearTimeout(this.state.resetCopyText)
	}

	getMessage(post, kit, outputAsString) {
		const user = this.props.user
		const isOwnPost = user.uid === post.uid
		const params = {
			outputAsString,
			nl: { display: () => (outputAsString ? '\n' : <br />) },
			profileLink: shortUserUrl(post.userName),
			atBrands: getField(kit, 'items').map(item => `@${getField(item, 'item.brandKey')}`).filter((v, i, a) => a.indexOf(v) === i).join(' '),
			link: this.postUrl(),
			postUser: post.userName,
			shareUser: user.userProfile.fullName
		}
		return isOwnPost ?
			this.context.translate('Check out my post in my Kitfit profile:{nl}{nl}{profileLink}{nl}{nl}{atBrands}{nl}{nl}Post link: {link}{nl}{nl}{shareUser}', params) :
			this.context.translate('Check out this post from {postUser}\'s Kitfit profile:{nl}{nl}{profileLink}{nl}{nl}{atBrands}{nl}{nl}Post link: {link}{nl}{nl}{shareUser}', params)
	}

	downloadImageBinder(url, imageIndex) {
		return () => {
			if (deviceDetect.isIos()) {
				this.props.appRedirect(`/imagedownload/?src=${encodeURIComponent(url)}`)
			} else {
				const download = (dataurl, filename) => {
					const a = document.createElement('a')
					a.href = dataurl
					a.setAttribute('download', filename)
					const b = document.createEvent('MouseEvents')
					b.initEvent('click', false, true)
					a.dispatchEvent(b)
					return false
				}

				downloadImages({ imageUrls: [url], skipSquarize: true }).then((imageInstances) => {
					const img = imageInstances[0]
					const canvas = global.document.createElement('canvas')
					const ctx = canvas.getContext('2d')
					canvas.width = img.width
					canvas.height = img.height
					ctx.drawImage(img, 0, 0)
					const dwLink = canvas.toDataURL('image/jpeg', 0.92)
					download(dwLink, this.context.translate('Kitfit {postTitle} - {imageIndex}.{extension}', { imageIndex, postTitle: this.postTitle, extention: 'jpeg' }))
				})
			}
		}
	}

	postUrl() {
		return `https://${settings.firebase.config.authDomain}/post/${this.state.postId}`
	}

	handleOnCopy() {
		clearTimeout(this.state.resetCopyText)
		this.setState({
			copyText: this.context.translate('Copied!'),
			resetCopyText: setTimeout(() => {
				this.setState({ copyText: this.context.translate('Copy text to clipboard') })
			}, 3000)
		})
	}

	shareFacebookKitUrl() {
		return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.postUrl())}`
	}

	render() {
		const post = this.props.post
		const kit = this.props.kitForSharing
		const user = this.props.user
		const isOwnPost = user && post && user.uid === post.uid
		return (
			<div className={styles.post}>
				{post && post.postId && kit && kit.kitId && this.state.images && (
					<>
						<section className={styles.pagePadding}>
							<p>{this.context.translate('To share a post on other networks, copy the text to your clipboard, download an image to your device and then go to the app you want to share the post with.')}</p>
						</section>

						<section className={styles.pagePadding}>
							<span className={styles.messageLabel}>{this.context.translate('Message')}</span>
							<p className={styles.message}>
								{this.getMessage(post, kit)}
							</p>
						</section>

						<section className={styles.sharing}>
							<div className={styles.buttonSurround}>
								<Button
									variant="contained"
									className={styles.button}
									href={mailToHref({
										body: this.getMessage(post, kit, true),
										subject: isOwnPost ? this.context.translate('Check out my post on Kitfit') : this.context.translate('Check out this post on Kitfit')
									})}
								>
									{this.context.translate('Share via email')}
								</Button>
							</div>
							<div className={styles.buttonSurround}>
								<CopyToClipboard text={this.getMessage(post, kit, true)} onCopy={this.handleOnCopy}>
									<Button variant="contained" type="button" className={styles.button}>
										{this.state.copyText}
									</Button>
								</CopyToClipboard>
							</div>
						</section>

						{/* Note: the order of the text copy and image is important, as on iOS you go to a different page */}

						<section className={styles.pagePadding}>
							<span className={styles.messageLabel}>{this.context.translate('Choose an image')}</span>
							<div className={styles.imageSurround}>
								<ImageCarousel
									images={this.state.images}
									onChange={(imageIndex) => {
										this.setState({ imageIndex })
									}}
								/>
							</div>
							<div className={styles.buttonWrapper}>
								<div className={styles.buttonSurround}>
									<Button
										variant="contained"
										classes={{ root: styles.button }}
										onClick={this.downloadImageBinder(this.state.images[this.state.imageIndex], this.state.imageIndex)}
									>
										{this.context.translate('Download Image')}
									</Button>
								</div>
							</div>
						</section>

						{/* <section className={styles.sharing}>
							<a className={styles.link} href={this.shareFacebookKitUrl()} target="_blank" rel="noopener noreferrer">
								<Button variant="contained" type="button" className={styles.button}>
									{this.context.translate('Share to Facebook')}
								</Button>
							</a>
						</section> */}
					</>
				)}
			</div>
		)
	}
}

PostShare.contextTypes = {
	translate: PropTypes.func
}

PostShare.propTypes = {
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	appHeading: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	postFetch: PropTypes.func.isRequired,
	kitShareFetch: PropTypes.func.isRequired,
	post: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kit: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitForSharing: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	user: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
}

PostShare.defaultProps = {
	post: undefined,
	kitForSharing: undefined,
	kit: undefined,
	kitDraft: undefined
}

export const testing = {
	PostShare
}

export default connectPropTypes(PostShare)



// WEBPACK FOOTER //
// ./src/PostShare.jsx